import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Elements, ExpressCheckoutElement, useElements, useStripe } from '@stripe/react-stripe-js';
import {
  loadStripe,
  ExpressCheckoutPaymentMethodsOption,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent
} from '@stripe/stripe-js';
import { createWalletSetupIntent } from '@uplift/api';
import { useStatesQuery } from '@uplift/queries';
import { AddressType } from '@uplift/types';
import { environment } from '../../../environments/environment';

// Deferred to first mount so merely importing this module doesn't trigger a Stripe.js load.
let cachedStripePromise: ReturnType<typeof loadStripe> | null = null;

const getStripePromise = () => {
  if (cachedStripePromise === null && environment.stripePublishableKey) {
    cachedStripePromise = loadStripe(environment.stripePublishableKey);
  }

  return cachedStripePromise;
};

const WALLET_PAYMENT_METHODS: ExpressCheckoutPaymentMethodsOption = {
  applePay: 'auto',
  googlePay: 'auto',
  amazonPay: 'never',
  link: 'never',
  paypal: 'never',
  klarna: 'never'
};

export type WalletPaymentResult = {
  paymentMethodId: string;
  billingAddress: AddressType;
};

type WalletButtonProps = {
  onWalletPayment: (result: WalletPaymentResult) => Promise<void>;
  onAvailabilityChange?: (available: boolean) => void;
};

const WalletExpressCheckout: React.VFC<WalletButtonProps> = ({ onWalletPayment, onAvailabilityChange }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [ walletAvailable, setWalletAvailable ] = useState(false);
  const { data: states } = useStatesQuery();

  const onReady = (event: StripeExpressCheckoutElementReadyEvent) => {
    const available = !!(event.availablePaymentMethods?.applePay || event.availablePaymentMethods?.googlePay);

    setWalletAvailable(available);
    onAvailabilityChange?.(available);
  };

  const onConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    if (!stripe || !elements) {
      return;
    }

    const { error: submitError } = await elements.submit();

    if (submitError) {
      event.paymentFailed();
      return;
    }

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {},
      redirect: 'if_required'
    });

    if (error || !setupIntent?.payment_method) {
      event.paymentFailed();
      return;
    }

    const billingDetails = event.billingDetails;
    const stateAbbreviation = billingDetails?.address?.state ?? '';
    const stateId = states?.find(state => state.shortName.toUpperCase() === stateAbbreviation.toUpperCase())?.id ?? '';

    await onWalletPayment({
      paymentMethodId: setupIntent.payment_method as string,
      billingAddress: {
        address1: billingDetails?.address?.line1 ?? '',
        address2: billingDetails?.address?.line2 ?? '',
        city: billingDetails?.address?.city ?? '',
        state: stateId,
        zip: billingDetails?.address?.postal_code ?? ''
      }
    });
  };

  return (
    <div style={{ display: walletAvailable ? 'block' : 'none' }}>
      <ExpressCheckoutElement
        options={{
          paymentMethods: WALLET_PAYMENT_METHODS,
          buttonType: { googlePay: 'plain', applePay: 'plain' }
        }}
        onReady={onReady}
        onConfirm={onConfirm}
      />
    </div>
  );
};

export const WalletButton: React.VFC<WalletButtonProps> = props => {
  const { onAvailabilityChange } = props;
  const [ clientSecret, setClientSecret ] = useState<string | null>(null);
  const [ stripePromise, setStripePromise ] = useState<ReturnType<typeof loadStripe> | null>(null);
  const [ stripeLoadFailed, setStripeLoadFailed ] = useState(false);

  useEffect(() => {
    const promise = getStripePromise();

    if (!promise) {
      onAvailabilityChange?.(false);
      return;
    }

    promise.catch(error => {
      // Stripe.js failed to load (network issue, ad blocker, etc) — fall back to the card form.
      Sentry.captureException(error, {
        tags: { component: 'WalletButton' },
        extra: { message: 'Stripe.js failed to load; falling back to typed-card form' }
      });
      setStripeLoadFailed(true);
      onAvailabilityChange?.(false);
    });

    setStripePromise(promise);
    createWalletSetupIntent()
      .then(result => setClientSecret(result.clientSecret))
      .catch(() => onAvailabilityChange?.(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (stripeLoadFailed || !stripePromise || !clientSecret) {
    return null;
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <WalletExpressCheckout {...props} />
    </Elements>
  );
};
